import { ExerciseDetail } from "../../../components/AthleteDashboard";
import {
	hasContent,
	getForTimeRoundsPrefix,
	getEmomRoundLabel,
	formatEmomItemSummary,
	resolveConditioningResultPresentation,
} from "../../../lib/conditioningResultUtils";
import {
	formatStrengthRepResult,
	getStrengthPrescriptionRepsForSet,
	isDistanceStrengthReps,
	isTimedStrengthReps,
	RUNNING_TYPES,
} from "../../../lib/programBuilderUtils";
import { formatEmomHeadline } from "../../../lib/emomSummary";

const isMeaningful = (value) => {
	if (!hasContent(value)) return false;
	const s = String(value).trim();
	return s !== "0" && s !== "0.0";
};

const formatSeconds = (totalSeconds) => {
	if (!isMeaningful(totalSeconds)) return null;
	const sec = Number(totalSeconds);
	if (!Number.isFinite(sec) || sec < 0) return null;
	const m = Math.floor(sec / 60);
	const s = Math.round(sec % 60);
	return `${m}:${String(s).padStart(2, "0")}`;
};

// I tempi di tiro sono secondi coi centesimi (4.82s), non MM:SS: fra 4.82 e 4.89
// c'è la differenza fra due piazzamenti, quindi niente arrotondamento al secondo.
const formatShotTime = (value) => {
	const n = Number(value);
	if (!Number.isFinite(n) || n < 0) return null;
	return `${n.toFixed(2)}s`;
};

const formatHitFactor = (value) => {
	const n = Number(value);
	if (!Number.isFinite(n)) return null;
	return n.toFixed(2);
};

const formatPoints = (value) => {
	const n = Number(value);
	if (!Number.isFinite(n)) return null;
	return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

const formatPct = (value) => {
	const n = Number(value);
	if (!Number.isFinite(n)) return null;
	return `${Math.round(n)}%`;
};

// { A: 6, C: 1 } → "A6 · C1"; scarta i codici a zero.
const formatZoneCounts = (counts) => {
	if (!counts || typeof counts !== "object") return "";
	return Object.entries(counts)
		.filter(([, count]) => Number(count) > 0)
		.map(([code, count]) => `${code}${count}`)
		.join(" · ");
};

// "production_optics" → "Production optics", come chip di contesto leggibile.
const humanizeToken = (value) => {
	const s = String(value || "").trim();
	if (!s) return "";
	const spaced = s.replace(/_/g, " ");
	return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

function formatSchemaValue(rawValue, unit) {
	if (rawValue === null || rawValue === undefined || rawValue === "")
		return null;
	const u = String(unit || "").toLowerCase();
	if (u === "minutes_seconds" || u === "time") {
		const total = Number(rawValue);
		if (Number.isFinite(total) && total >= 0) {
			const m = Math.floor(total / 60);
			const s = Math.round(total % 60);
			return `${m}:${String(s).padStart(2, "0")}`;
		}
	}
	if (u === "kg") return `${rawValue} kg`;
	if (u === "meters") return `${rawValue} m`;
	if (u === "reps") return `${rawValue} reps`;
	return String(rawValue);
}

function ConditioningResultBlock({
	exercise,
	result,
	language,
	isCoachView = false,
}) {
	const view = resolveConditioningResultPresentation({
		result,
		prescriptionExercise: exercise,
		programExercise: exercise,
		language,
	});
	const {
		condType,
		emomRounds,
		rounds,
		roundsLabel,
		timeCap,
		interval,
		displayScore,
		amrapScoreLabel,
		displayNotes,
	} = view;

	const forTimePrefix =
		condType === "for_time"
			? getForTimeRoundsPrefix(rounds, roundsLabel, language)
			: "";

	// Score "principale" da mostrare nella riga score: privilegia AMRAP formatted
	// se applicabile, altrimenti displayScore generico.
	const primaryScore =
		condType === "amrap" && amrapScoreLabel ? amrapScoreLabel : displayScore;

	// Schema-based result: itera result_schema dell'esercizio e mostra i valori
	// loggati da result.schema_values.
	const schemaFields = Array.isArray(exercise?.result_schema)
		? exercise.result_schema
		: [];
	const schemaValues =
		result?.schema_values && typeof result.schema_values === "object"
			? result.schema_values
			: null;
	const schemaRows = schemaValues
		? schemaFields
				.map((field) => {
					const fid = field?.field_id || field?.name;
					if (!fid) return null;
					const raw = schemaValues[fid];
					const formatted = formatSchemaValue(raw, field?.unit);
					if (!formatted) return null;
					// Label visibile: ConditioningResultSchemaEditor usa `title` come
					// etichetta umana del campo. Fallback su altri campi storici.
					const label =
						field?.title ||
						field?.label ||
						field?.name ||
						field?.display_label ||
						"Valore";
					return { label, value: formatted };
				})
				.filter(Boolean)
		: [];

	// Flag per capire se almeno UN dato è renderizzabile (oltre al label tipo).
	const hasAnyData =
		hasContent(primaryScore) ||
		isMeaningful(result?.time_seconds) ||
		isMeaningful(result?.weight_kg) ||
		hasContent(result?.reps_text) ||
		(result?.interval_scores || []).length > 0 ||
		isMeaningful(result?.reps) ||
		schemaRows.length > 0 ||
		(condType === "emom" && emomRounds.length > 0) ||
		isMeaningful(result?.rpe) ||
		hasContent(result?.scaling) ||
		hasContent(displayNotes) ||
		hasContent(result?.notes);

	return (
		<div className="space-y-1.5 text-xs">
			{/* Header tipo conditioning */}
			<div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
				{condType === "for_time" && (
					<span className="font-medium text-foreground">
						{forTimePrefix ? `${forTimePrefix} ` : ""}For Time
						{timeCap && (
							<span className="text-muted-foreground font-normal">
								{" "}
								· TC {Math.floor(timeCap / 60)}'
							</span>
						)}
					</span>
				)}
				{condType === "amrap" && (
					<span className="font-medium text-foreground">
						{timeCap ? `${Math.floor(timeCap / 60)}' ` : ""}AMRAP
					</span>
				)}
				{condType === "emom" && (
					<span className="font-medium text-foreground">
						{formatEmomHeadline(
							{ rounds, interval_seconds: interval },
							language,
						)}
					</span>
				)}
				{condType === "hiit" && (
					<span className="font-medium text-foreground">HIIT</span>
				)}
				{(condType === "free" || condType === "custom" || !condType) && (
					<span className="font-medium text-foreground">Conditioning</span>
				)}
			</div>

			{/* Coach view: mostra SOLO i dati dettagliati, NON il score recap (già in card) */}
			{isCoachView ? (
				<>
					{/* AMRAP: mostra il punteggio con i round calcolati (non presente nel
					    recap card, che riporta solo il valore grezzo) */}
					{amrapScoreLabel && (
						<div className="text-volt">
							<span className="text-muted-foreground">Risultato: </span>
							{amrapScoreLabel}
						</div>
					)}
					{/* Dettagli senza recap score */}
					{isMeaningful(result?.time_seconds) && (
						<div className="text-volt">
							<span className="text-muted-foreground">Tempo: </span>
							{formatSeconds(result.time_seconds)}
						</div>
					)}
					{isMeaningful(result?.weight_kg) && (
						<div className="text-volt">
							<span className="text-muted-foreground">Carico: </span>
							{result.weight_kg} kg
						</div>
					)}
					{(hasContent(result?.reps_text) || isMeaningful(result?.reps)) && (
						<div>
							<span className="text-muted-foreground">Reps: </span>
							{result.reps_text || result.reps}
						</div>
					)}
					{(result?.interval_scores || []).length > 0 && (
						<div>
							<span className="text-muted-foreground">Ripetute: </span>
							{result.interval_scores.join(" · ")}
						</div>
					)}
					{schemaRows.length > 0 && (
						<div className="space-y-0.5">
							{schemaRows.map((row, j) => (
								<div key={j}>
									<span className="text-muted-foreground">{row.label}: </span>
									<span className="text-foreground">{row.value}</span>
								</div>
							))}
						</div>
					)}
					{condType === "emom" && emomRounds.length > 0 && (
						<div className="space-y-1 pt-1">
							{emomRounds.map((round, j) => (
								<div
									key={round.round_id || j}
									className="border border-amber-400/20 bg-amber-400/5 rounded-sm px-2 py-1.5"
								>
									<div className="text-[10px] uppercase tracking-wider text-amber-400 font-medium mb-0.5">
										{getEmomRoundLabel(round, j, language)}
									</div>
									{(round.items || []).map((item, k) => (
										<span
											key={item.item_id || `${j}-${k}`}
											className="block text-[11px] text-muted-foreground"
										>
											{formatEmomItemSummary(item, language)}
										</span>
									))}
								</div>
							))}
						</div>
					)}
					{isMeaningful(result?.rpe) && (
						<div>
							<span className="text-muted-foreground">RPE:</span> {result.rpe}
						</div>
					)}
					{hasContent(result?.scaling) && (
						<div>
							<span className="text-muted-foreground">Scaling:</span>{" "}
							{result.scaling}
						</div>
					)}
					{hasContent(displayNotes) && (
						<div className="pt-1 italic text-muted-foreground">
							"{displayNotes}"
						</div>
					)}
					{!hasAnyData && (
						<div className="text-muted-foreground/60 italic">
							Nessun dettaglio registrato.
						</div>
					)}
				</>
			) : (
				<>
					{/* Original athlete view: mostra score recap + dettagli */}
					{/* Dati registrati — mostro qualunque campo abbia valore, indipendentemente dal condType */}
					{hasContent(primaryScore) && (
						<div className="text-volt">
							<span className="text-muted-foreground">Score: </span>
							{primaryScore}
						</div>
					)}
					{isMeaningful(result?.time_seconds) && (
						<div className="text-volt">
							<span className="text-muted-foreground">Tempo: </span>
							{formatSeconds(result.time_seconds)}
						</div>
					)}
					{isMeaningful(result?.weight_kg) && (
						<div className="text-volt">
							<span className="text-muted-foreground">Carico: </span>
							{result.weight_kg} kg
						</div>
					)}
					{(hasContent(result?.reps_text) || isMeaningful(result?.reps)) && (
						<div>
							<span className="text-muted-foreground">Reps: </span>
							{result.reps_text || result.reps}
						</div>
					)}
					{(result?.interval_scores || []).length > 0 && (
						<div>
							<span className="text-muted-foreground">Ripetute: </span>
							{result.interval_scores.join(" · ")}
						</div>
					)}

					{/* Schema-based result (es. conditioning custom con campi multipli) */}
					{schemaRows.length > 0 && (
						<div className="space-y-0.5">
							{schemaRows.map((row, j) => (
								<div key={j}>
									<span className="text-muted-foreground">{row.label}: </span>
									<span className="text-foreground">{row.value}</span>
								</div>
							))}
						</div>
					)}

					{/* Round EMOM con item logged (dalla prescrizione) */}
					{condType === "emom" && emomRounds.length > 0 && (
						<div className="space-y-1 pt-1">
							{emomRounds.map((round, j) => (
								<div
									key={round.round_id || j}
									className="border border-amber-400/20 bg-amber-400/5 rounded-sm px-2 py-1.5"
								>
									<div className="text-[10px] uppercase tracking-wider text-amber-400 font-medium mb-0.5">
										{getEmomRoundLabel(round, j, language)}
									</div>
									{(round.items || []).map((item, k) => (
										<span
											key={item.item_id || `${j}-${k}`}
											className="block text-[11px] text-muted-foreground"
										>
											{formatEmomItemSummary(item, language)}
										</span>
									))}
								</div>
							))}
						</div>
					)}

					{/* RPE / scaling / notes */}
					{isMeaningful(result?.rpe) && (
						<div>
							<span className="text-muted-foreground">RPE:</span> {result.rpe}
						</div>
					)}
					{hasContent(result?.scaling) && (
						<div>
							<span className="text-muted-foreground">Scaling:</span>{" "}
							{result.scaling}
						</div>
					)}
					{hasContent(displayNotes) && (
						<div className="pt-1 italic text-muted-foreground">
							"{displayNotes}"
						</div>
					)}

					{/* Stato vuoto esplicito: il result esiste ma è privo di dati visualizzabili.
					    Indica un possibile bug di salvataggio o un risultato troncato. */}
					{!hasAnyData && (
						<div className="text-muted-foreground/60 italic">
							Risultato registrato senza dettagli numerici. Apri il dettaglio
							per verificare.
						</div>
					)}
				</>
			)}
		</div>
	);
}

function StrengthResultBlock({ result, prescription, isCoachView = false }) {
	const setRows = Array.isArray(result.result_set_rows)
		? result.result_set_rows
		: [];
	const supersetDetails = Array.isArray(result.superset_details)
		? result.superset_details
		: [];
	const hasDetailed = setRows.length > 0 || supersetDetails.length > 0;
	const getDisplaySetNumber = (row, idx) => {
		const rawIndex = Number(row?.row_index);
		if (Number.isFinite(rawIndex) && rawIndex >= 0) {
			return rawIndex + 1;
		}
		return idx + 1;
	};

	return (
		<div className="space-y-1.5 text-xs">
			{/* Superset: una riga per sub-esercizio */}
			{supersetDetails.length > 0 && (
				<div className="space-y-1">
					<div className="text-[10px] uppercase tracking-wider text-muted-foreground">
						Superset
					</div>
					{supersetDetails.map((d, j) => (
						<div
							key={j}
							className="flex flex-wrap items-baseline gap-x-2 bg-secondary/40 rounded-sm px-2 py-1.5"
						>
							<span className="font-medium text-foreground">
								<span className="text-volt mr-1">
									{String.fromCharCode(65 + j)}:
								</span>
								{d.exercise_name || "—"}
							</span>
							{isMeaningful(d.weight_kg) && (
								<span className="text-volt">{d.weight_kg} kg</span>
							)}
							{isMeaningful(d.reps) && (
								<span className="text-muted-foreground">· {d.reps} reps</span>
							)}
							{isMeaningful(d.rpe) && (
								<span className="text-acid">· RPE {d.rpe}</span>
							)}
						</div>
					))}
				</div>
			)}

			{/* Set-by-set strength */}
			{setRows.length > 0 && (
				<div className="space-y-1">
					<div className="text-[10px] uppercase tracking-wider text-muted-foreground">
						Set eseguiti
					</div>
					{setRows.map((row, j) => {
						const reps = hasContent(row.reps_text) ? row.reps_text : row.reps;
						const rowIndex = Number.isInteger(Number(row?.row_index))
							? Number(row.row_index)
							: j;
						const prescribedReps = getStrengthPrescriptionRepsForSet(
							prescription,
							rowIndex,
						);
						if (
							!isMeaningful(row.weight_kg) &&
							!isMeaningful(reps) &&
							!isMeaningful(row.rpe)
						)
							return null;
						return (
							<div
								key={j}
								className="flex flex-wrap items-baseline gap-x-2 bg-secondary/40 rounded-sm px-2 py-1.5"
							>
								<span className="text-muted-foreground w-12">
									Set {getDisplaySetNumber(row, j)}
								</span>
								{isMeaningful(row.weight_kg) && (
									<span className="text-volt font-medium">
										{row.weight_kg} kg
									</span>
								)}
								{isMeaningful(reps) && (
									<span className="text-muted-foreground">
										· {formatStrengthRepResult(reps, prescribedReps)}
									</span>
								)}
								{isMeaningful(row.rpe) && (
									<span className="text-acid">· RPE {row.rpe}</span>
								)}
							</div>
						);
					})}
				</div>
			)}

			{/* Riepilogo sintetico (se NO detailed) */}
			{!hasDetailed && (
				<div className="space-y-0.5">
					{isMeaningful(result.weight_kg) && (
						<div>
							<span className="text-muted-foreground">Carico:</span>{" "}
							<span className="text-volt font-medium">
								{result.weight_kg} kg
							</span>
						</div>
					)}
					{(hasContent(result.reps_text) || isMeaningful(result.reps)) && (
						<div>
							<span className="text-muted-foreground">
								{isTimedStrengthReps(
									prescription?.set_rows?.[0]?.reps ?? prescription?.reps,
								)
									? "Tempo:"
									: isDistanceStrengthReps(
												prescription?.set_rows?.[0]?.reps ?? prescription?.reps,
										  )
										? "Distanza:"
										: "Reps:"}
							</span>{" "}
							{isTimedStrengthReps(
								prescription?.set_rows?.[0]?.reps ?? prescription?.reps,
							) ||
							isDistanceStrengthReps(
								prescription?.set_rows?.[0]?.reps ?? prescription?.reps,
							)
								? formatStrengthRepResult(
										result.reps_text || result.reps,
										prescription?.set_rows?.[0]?.reps ?? prescription?.reps,
									)
								: result.reps_text || result.reps}
						</div>
					)}
					{isMeaningful(result.time_seconds) && (
						<div>
							<span className="text-muted-foreground">Tempo:</span>{" "}
							{formatSeconds(result.time_seconds)}
						</div>
					)}
					{hasContent(result.score) && (
						<div>
							<span className="text-muted-foreground">Score:</span>{" "}
							{result.score}
						</div>
					)}
				</div>
			)}

			{/* RPE / scaling / notes (comuni anche con detailed) */}
			{!hasDetailed && isMeaningful(result.rpe) && (
				<div>
					<span className="text-muted-foreground">RPE:</span> {result.rpe}
				</div>
			)}
			{hasContent(result.scaling) && (
				<div>
					<span className="text-muted-foreground">Scaling:</span>{" "}
					{result.scaling}
				</div>
			)}
			{hasContent(result.notes) && (
				<div className="pt-1 italic text-muted-foreground">
					"{result.notes}"
				</div>
			)}
		</div>
	);
}

function RunningResultBlock({ exercise, result }) {
	const runningType = String(exercise?.running_type || "")
		.trim()
		.toLowerCase();
	const typeLabel = RUNNING_TYPES.find(
		(item) => item.value === runningType,
	)?.label;
	const titleLabel =
		runningType && runningType !== "custom" && typeLabel
			? `Running - ${typeLabel}`
			: "Running";

	const intervalScores = Array.isArray(result?.interval_scores)
		? result.interval_scores.filter((value) => hasContent(value))
		: [];
	const intervalText =
		intervalScores.join(" · ") ||
		(hasContent(result?.reps_text) ? result.reps_text : "");
	// I parziali sono ripetute (interval_rows) o tratti (fartlek_segments):
	// la prescrizione dice quali, e l'etichetta segue.
	const hasIntervalRows =
		Array.isArray(exercise?.interval_rows) && exercise.interval_rows.length > 0;
	const intervalLabel = hasIntervalRows ? "Ripetute" : "Tratti";
	const timeText = formatSeconds(result?.time_seconds);
	const distanceText = hasContent(result?.score) ? result.score : "";
	// Passo medio dell'uscita, CALCOLATO da tempo e distanza registrati: non
	// e' un dato dell'orologio e la vista lo dice. Senza uno dei due, niente.
	const averagePace = (() => {
		const seconds = Number(result?.time_seconds);
		const km = Number(result?.score);
		if (!Number.isFinite(seconds) || !Number.isFinite(km) || seconds <= 0 || km <= 0)
			return "";
		const perKm = seconds / km;
		const m = Math.floor(perKm / 60);
		const s = Math.round(perKm % 60);
		return `${m}:${String(s === 60 ? 0 : s).padStart(2, "0")}/km`;
	})();
	const avgHr = isMeaningful(result?.avg_heart_rate) ? result.avg_heart_rate : "";
	const maxHr = isMeaningful(result?.max_heart_rate) ? result.max_heart_rate : "";

	return (
		<div className="space-y-1.5 text-xs">
			<div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
				<span className="font-medium text-foreground">{titleLabel}</span>
			</div>
			{timeText && (
				<div>
					<span className="text-muted-foreground">Tempo: </span>
					{timeText}
				</div>
			)}
			{distanceText && (
				<div>
					<span className="text-muted-foreground">Distanza: </span>
					{distanceText} km
				</div>
			)}
			{averagePace && (
				<div>
					<span className="text-muted-foreground">Passo medio: </span>
					{averagePace}
					<span className="text-muted-foreground"> (calcolato)</span>
				</div>
			)}
			{(avgHr || maxHr) && (
				<div>
					<span className="text-muted-foreground">FC: </span>
					{avgHr ? `media ${avgHr}` : ""}
					{avgHr && maxHr ? " · " : ""}
					{maxHr ? `max ${maxHr}` : ""}
				</div>
			)}
			{intervalText && (
				<div>
					<span className="text-muted-foreground">{intervalLabel}: </span>
					{intervalText}
				</div>
			)}
			{isMeaningful(result?.rpe) && (
				<div>
					<span className="text-muted-foreground">RPE: </span>
					{result.rpe}
				</div>
			)}
			{hasContent(result?.notes) && (
				<div className="pt-1 italic text-muted-foreground">
					"{result.notes}"
				</div>
			)}
		</div>
	);
}

/**
 * Risultato di tiro (dry fire / live fire). I valori veri vivono in
 * `result.shooting` (hit factor, punti, stringhe) — ricalcolati server-side —
 * non nei campi kg/reps/tempo dello strength, quindi serve un blocco dedicato
 * o il coach vede una riga vuota.
 */
function ShootingResultBlock({ exercise, result, isCoachView = false }) {
	const shooting =
		result?.shooting && typeof result.shooting === "object"
			? result.shooting
			: null;
	const mode =
		shooting?.mode ||
		(exercise?.exercise_type === "dry_fire" ? "dry_fire" : "live_fire");
	const isFree = shooting?.prescription_format === "free";
	const modeLabel = mode === "dry_fire" ? "Dry fire" : "Live fire";

	// Prescritto vs eseguito. **Non c'era da nessuna parte**: il coach vedeva
	// il risultato senza sapere se bastava. Un hit factor di 4.2 non dice
	// niente finché non sai che l'obiettivo era 5.0, e 38 colpi su 36 previsti
	// e' un dato di allestimento, non un dettaglio.
	const prescribed = exercise?.shooting || {};
	// `rounds_planned` sono i colpi **per serie**; `rounds_fired` e' il totale
	// sparato. Il confronto va fatto sul totale previsto (colpi × serie), o
	// quattro serie da 12 colpi uscivano come «48 / 12» in ambra.
	const seriesDone = (shooting?.series || shooting?.strings || []).length;
	const seriesPlanned =
		(Array.isArray(prescribed.series) && prescribed.series.length) ||
		prescribed.series_planned ||
		prescribed.strings_planned ||
		0;
	const roundsExpected = prescribed.rounds_planned
		? prescribed.rounds_planned * Math.max(1, seriesPlanned)
		: 0;
	const comparisons = [
		roundsExpected > 0 && [
			"Colpi",
			`${shooting?.rounds_fired ?? "—"} / ${roundsExpected}`,
			shooting?.rounds_fired != null && shooting.rounds_fired !== roundsExpected,
		],
		seriesPlanned > 0 && [
			"Serie",
			`${seriesDone} / ${seriesPlanned}`,
			seriesDone < seriesPlanned,
		],
		prescribed.par_time_seconds &&
			shooting?.best_time_seconds != null && [
				"Par",
				`${formatShotTime(shooting.best_time_seconds)} / ${formatShotTime(prescribed.par_time_seconds)}`,
				shooting.best_time_seconds > prescribed.par_time_seconds,
			],
	].filter(Boolean);
	const goal = prescribed.athlete_goal || prescribed.success_criteria;

	// Contesto congelato al salvataggio: division, power factor, classe.
	const contextChips = [
		humanizeToken(shooting?.division),
		shooting?.power_factor
			? shooting.power_factor === "major"
				? "Major"
				: "Minor"
			: "",
		shooting?.class_at_time ? String(shooting.class_at_time).toUpperCase() : "",
	].filter(Boolean);

	// `series` è il nome attuale, `strings` quello dei documenti più vecchi.
	const strings = Array.isArray(shooting?.series)
		? shooting.series
		: Array.isArray(shooting?.strings)
			? shooting.strings
			: [];
	const times = Array.isArray(shooting?.times)
		? shooting.times.map((t) => formatShotTime(t)).filter(Boolean)
		: [];

	const hf = formatHitFactor(shooting?.hit_factor);
	const totalPoints = formatPoints(shooting?.total_points);
	const totalTime = formatShotTime(shooting?.total_time_seconds);
	const accuracy = formatPct(shooting?.accuracy_pct);
	const hitRate = formatPct(shooting?.hit_rate_pct);
	const bestTime = formatShotTime(shooting?.best_time_seconds);
	const avgTime = formatShotTime(shooting?.avg_time_seconds);
	const successRate = formatPct(shooting?.success_rate_pct);
	const selfRating = Number(shooting?.self_rating) || null;

	const hasAnyData =
		!!hf ||
		!!totalPoints ||
		!!totalTime ||
		!!bestTime ||
		!!avgTime ||
		times.length > 0 ||
		strings.length > 0 ||
		!!selfRating ||
		hasContent(result?.notes);

	return (
		<div className="space-y-1.5 text-xs">
			<div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
				<span className="font-medium text-foreground">{modeLabel}</span>
				{contextChips.map((chip, i) => (
					<span
						key={i}
						className="text-[10px] uppercase tracking-wider text-muted-foreground"
					>
						{chip}
					</span>
				))}
			</div>

			{/* Prescritto vs eseguito, solo lato coach: nel diario l'atleta ha
			    già la prescrizione sopra, e ripeterla è rumore. */}
			{isCoachView && (comparisons.length > 0 || goal) && (
				<div className="flex flex-wrap items-center gap-x-3 gap-y-1 pb-0.5">
					{comparisons.map(([label, value, off]) => (
						<span key={label} className="text-[11px]">
							<span className="text-muted-foreground">{label} </span>
							<span className={off ? "text-amber-400" : "text-foreground"}>
								{value}
							</span>
						</span>
					))}
					{goal && (
						<span className="text-[11px]">
							<span className="text-muted-foreground">Obiettivo </span>
							<span className="text-foreground">{goal}</span>
						</span>
					)}
				</div>
			)}

			{/* Formato libero: non c'è punteggio, solo l'autovalutazione. */}
			{isFree ? (
				selfRating ? (
					<div>
						<span className="text-muted-foreground">Autovalutazione: </span>
						<span className="text-volt font-medium">{selfRating}/5</span>
					</div>
				) : null
			) : mode === "dry_fire" ? (
				<>
					{bestTime && (
						<div className="text-volt">
							<span className="text-muted-foreground">Miglior tempo: </span>
							<span className="font-medium">{bestTime}</span>
						</div>
					)}
					{avgTime && (
						<div>
							<span className="text-muted-foreground">Media: </span>
							{avgTime}
						</div>
					)}
					{shooting?.par_time_seconds ? (
						<div>
							<span className="text-muted-foreground">Par: </span>
							{formatShotTime(shooting.par_time_seconds)}
							{successRate ? ` · ${successRate} dentro il par` : ""}
						</div>
					) : successRate ? (
						<div>
							<span className="text-muted-foreground">Dentro il par: </span>
							{successRate}
						</div>
					) : null}
					{times.length > 0 && (
						<div>
							<span className="text-muted-foreground">Tempi: </span>
							{times.join(" · ")}
						</div>
					)}
					{selfRating && (
						<div>
							<span className="text-muted-foreground">Autovalutazione: </span>
							{selfRating}/5
						</div>
					)}
				</>
			) : (
				<>
					{/* Live fire: hit factor e punti in evidenza. */}
					{(hf || totalPoints || totalTime) && (
						<div className="flex flex-wrap items-baseline gap-x-2">
							{hf && <span className="text-volt font-semibold">HF {hf}</span>}
							{totalPoints && (
								<span className="text-foreground">
									<span className="text-muted-foreground">· </span>
									{totalPoints} punti
								</span>
							)}
							{totalTime && (
								<span className="text-muted-foreground">· {totalTime}</span>
							)}
						</div>
					)}
					{(accuracy || hitRate || shooting?.rounds_fired) && (
						<div className="text-muted-foreground">
							{accuracy ? `${accuracy} accuratezza` : ""}
							{accuracy && (hitRate || shooting?.rounds_fired) ? " · " : ""}
							{hitRate ? `${hitRate} a bersaglio` : ""}
							{hitRate && shooting?.rounds_fired ? " · " : ""}
							{shooting?.rounds_fired ? `${shooting.rounds_fired} colpi` : ""}
						</div>
					)}
					{strings.length > 0 && (
						<div className="space-y-1 pt-1">
							{strings.map((str, j) => {
								const strTime = formatShotTime(str?.time_seconds);
								const strHf = formatHitFactor(str?.hit_factor);
								const strPoints = formatPoints(str?.points);
								const hits = formatZoneCounts(str?.hits);
								const penalties = formatZoneCounts(str?.penalties);
								return (
									<div
										key={str?.string_index ?? j}
										className="flex flex-wrap items-baseline gap-x-2 bg-secondary/40 rounded-sm px-2 py-1.5"
									>
										<span className="text-muted-foreground shrink-0">
											{/* La distanza, quando c'è: è quella che rende il
											    punteggio confrontabile fra una serie e l'altra. */}
											{str?.label ||
												(str?.distance_m ? `${str.distance_m} m` : null) ||
												`#${str?.series_index ?? str?.string_index ?? j + 1}`}
										</span>
										{strTime && (
											<span className="text-foreground">{strTime}</span>
										)}
										{hits && (
											<span className="text-muted-foreground">· {hits}</span>
										)}
										{penalties && (
											<span className="text-red-400/90">· {penalties}</span>
										)}
										{strPoints && (
											<span className="text-muted-foreground">
												· {strPoints} pt
											</span>
										)}
										{strHf && <span className="text-volt">· HF {strHf}</span>}
										{/* Dove si sono persi i punti. "Due Charlie su T4"
										    è un'indicazione di allenamento; "due Charlie"
										    non lo è. */}
										{(str?.targets || []).length > 0 && (
											<span className="basis-full text-[11px] text-muted-foreground pt-0.5">
												{str.targets
													.map((target) => {
														const detail = [
															formatZoneCounts(target?.hits),
															formatZoneCounts(target?.penalties),
														]
															.filter(Boolean)
															.join(" ");
														return `T${target.target_no} ${detail || "—"}`;
													})
													.join("  ·  ")}
											</span>
										)}
									</div>
								);
							})}
						</div>
					)}
				</>
			)}

			{hasContent(result?.notes) && (
				<div className="pt-1 italic text-muted-foreground">
					"{result.notes}"
				</div>
			)}

			{!hasAnyData && (
				<div className="text-muted-foreground/60 italic">
					Nessun dettaglio di tiro registrato.
				</div>
			)}
		</div>
	);
}

/**
 * Accordion del Diario: prescrizione (riusa ExerciseDetail della Dashboard)
 * + "Tuo risultato" che gestisce conditioning / superset / set-by-set / simple.
 *
 * @param {Object} exercise - Exercise object with result attached
 * @param {string} language - Language code (default: "it")
 * @param {boolean} isCoachView - If true, adjust labels and layout for coach viewing athlete result
 */
export function ExerciseDetailAccordion({
	exercise,
	language = "it",
	isCoachView = false,
}) {
	const result = exercise?.result;
	const isShootingExercise =
		exercise?.exercise_type === "dry_fire" ||
		exercise?.exercise_type === "live_fire";
	const isConditioningExercise =
		exercise?.exercise_type === "conditioning" ||
		exercise?.exercise_type === "running" ||
		hasContent(exercise?.conditioning_type) ||
		hasContent(result?.conditioning_type);

	return (
		<div
			className={`text-sm ${
				isCoachView ? "px-4 pb-4 pt-3 space-y-4" : "px-3 pb-3 pt-2 space-y-3"
			}`}
		>
			{/* Dettaglio prescrizione (riuso integrale da Dashboard) */}
			<div
				className={
					isCoachView
						? "rounded-sm border border-border/40 bg-background/20 px-3 py-3 space-y-2.5"
						: undefined
				}
			>
				{isCoachView && (
					<div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
						Prescrizione
					</div>
				)}
				<ExerciseDetail
					exercise={exercise}
					exerciseLibrary={{}}
					loadSuggestions={{}}
					hideScorePreview={isCoachView}
					onOpenMedia={undefined}
				/>
			</div>

			{/* Risultato — specifico Diario o Coach View */}
			{result && (
				<div
					className={
						isCoachView
							? "rounded-sm border border-volt/15 bg-volt/[0.04] px-3 py-3 space-y-2.5"
							: "pt-3 border-t border-border/40"
					}
				>
					<div
						className={`text-[10px] uppercase tracking-wider font-medium ${
							isCoachView ? "text-volt" : "text-volt/80"
						}`}
					>
						{isCoachView ? "Risultato atleta" : "Tuo risultato"}
					</div>
					{exercise?.exercise_type === "running" ? (
						<RunningResultBlock exercise={exercise} result={result} />
					) : isShootingExercise ? (
						<ShootingResultBlock
							exercise={exercise}
							result={result}
							isCoachView={isCoachView}
						/>
					) : isConditioningExercise ? (
						<ConditioningResultBlock
							exercise={exercise}
							result={result}
							language={language}
							isCoachView={isCoachView}
						/>
					) : (
						<StrengthResultBlock
							result={result}
							prescription={exercise}
							isCoachView={isCoachView}
						/>
					)}
				</div>
			)}
		</div>
	);
}
